import { CLEAR_BRAND_FAVICON, CLEAR_BRAND_NAME } from "@common-config/clear-brand";
import { isClearFlow } from "@common/utils/clear-flow";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const FAVICON_SELECTOR = 'link[rel~="icon"]';

/**
 * Owns everything brand-facing that lives OUTSIDE React's tree while the clear flow
 * is active: the `data-clear-flow` attribute on <html> (which drives the purple token
 * palette and the hidden Crisp bubble in globals-website.css), the tab title, and the
 * favicon. None of these can be reached by rendering.
 *
 * Titles are otherwise set per-page (`document.title = t(...)`) and those strings
 * pass through the i18n rebrand, so this only has to own the favicon plus the
 * "Blueticks App" default from the Helmet block. Both are restored on the way out
 * so the regular product keeps its identity in the same tab.
 */
export function useClearFlowBranding(): void {
  const location = useLocation();

  useEffect(() => {
    const root = document.documentElement;
    const favicon = document.querySelector<HTMLLinkElement>(FAVICON_SELECTOR);

    if (!isClearFlow(location.pathname)) {
      root.removeAttribute("data-clear-flow");
      return;
    }
    root.setAttribute("data-clear-flow", "true");

    const previousTitle = document.title;
    const previousHref = favicon?.getAttribute("href") ?? null;

    if (!previousTitle || previousTitle.includes("Blueticks")) {
      document.title = CLEAR_BRAND_NAME;
    }
    favicon?.setAttribute("href", CLEAR_BRAND_FAVICON);

    return () => {
      root.removeAttribute("data-clear-flow");
      if (previousHref !== null) favicon?.setAttribute("href", previousHref);
      if (document.title === CLEAR_BRAND_NAME) document.title = previousTitle;
    };
  }, [location.pathname]);

  // A page that sets its own title does so after this effect; re-assert the
  // brand for the default "Blueticks App" title that Helmet restores on nav.
  useEffect(() => {
    if (!isClearFlow(location.pathname)) return;
    const timer = window.setTimeout(() => {
      if (document.title.includes("Blueticks")) document.title = CLEAR_BRAND_NAME;
    }, 0);
    return () => window.clearTimeout(timer);
  });
}
